import { useState, useEffect, useMemo, useCallback } from "react";
import { fetchPersonalDetails, updatePersonalDetails } from "../utils/api.js";
import LoadingState from "../components/shared/LoadingState";
import CustomSelect from "../components/shared/CustomSelect";
import { cssUrl } from '../utils/cssVersion';
const personalStyles = cssUrl('newuseronboarding.css');

const VISA_TYPES = [
  "Citizen",
  "Permanent Residency",
  "Student Visa",
  "Bridging Visa",
  "Family & Partner Visa",
  "Visitor Visa",
  "Trade Visa",
  "Refugee Visa",
  "Other",
  "Temporary Graduate",
  "Temporary Activity",
];
const FIELDS = [
  { key: "recordId", label: null },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "dateOfBirth", label: "Date Of Birth" },
  { key: "policeCheckIssuedDate", label: "Police Check Issued Date" },
  { key: "visaType", label: "Visa Type" },
  { key: "visaExpiry", label: "Visa Expiry" },
  { key: "businessName", label: "Business Name" },
  { key: "abnNumber", label: "ABN Number" },
  { key: "tfnNumber", label: "TFN Number" },
  { key: "address", label: "Address" },
  { key: "superFundName", label: "Super Fund Name" },
  { key: "superFundAccountName", label: "Super Fund Account Name" },
  { key: "membershipNumber", label: "Membership Number" },
  { key: "workStatus", label: "Work Status" },
  { key: "bankAccountName", label: "Bank Account Name" },
  { key: "bsbNumber", label: "BSB Number" },
  { key: "accountNumber", label: "Account Number" },
];

const FIELD_KEYS = FIELDS.map((f) => f.key);
const FIELD_LABELS = Object.fromEntries(
  FIELDS.filter((f) => f.label).map((f) => [f.key, f.label])
);

const READONLY_FIELDS = new Set([
  "workStatus",
  "mobile",
  "dateOfBirth",
  "tfnNumber",
  "bankAccountName",
  "bsbNumber",
  "accountNumber",
]);
const DATE_FIELDS = new Set(["dateOfBirth", "policeCheckIssuedDate", "visaExpiry"]);

// Field render order in the form (above the banking divider)
const EDITABLE_FIELD_ORDER = [
  "workStatus",
  "email",
  "mobile",
  "dateOfBirth",
  "policeCheckIssuedDate",
  "visaType",
  "visaExpiry",
  "businessName",
  "abnNumber",
  "tfnNumber",
  "address",
  "superFundName",
  "superFundAccountName",
  "membershipNumber",
];

const BANKING_FIELD_ORDER = ["bankAccountName", "bsbNumber", "accountNumber"];

// Map a raw row (array) from the backend into a keyed object
const rowToRecord = (row) => {
  const record = {};
  FIELD_KEYS.forEach((key, i) => {
    record[key] = row[i] ?? "";
  });
  return record;
};

const ErrorIcon = () => (
  <svg fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

// Block space at the start of the input or after a trailing space
const handleKeyDown = (e) => {
  if (e.key === " ") {
    const val = e.target.value;
    if (val.length === 0 || val.endsWith(" ")) {
      e.preventDefault();
    }
  }
};


const SELECT_FIELDS = new Set(["visaType"]);
const SELECT_OPTIONS = {
  visaType: VISA_TYPES,
};
const PersonalDetails = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [records, setRecords] = useState([]);
  const [activeWorkStatus, setActiveWorkStatus] = useState(null); // "ABN" | "TFN" | null
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // Load stylesheet
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = personalStyles;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Toast auto-dismiss
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // Fetch on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchPersonalDetails();
        if (res.status === "success") {
          setRecords(res.data.map(rowToRecord));
        } else {
          throw new Error(res.message || "Failed to load personal details");
        }
      } catch (err) {
        setLoadError(err.message || "Failed to load personal details");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Which work statuses does the user have?
  const availableStatuses = useMemo(() => {
    const set = new Set();
    records.forEach((r) => {
      if (r.workStatus) set.add(r.workStatus);
    });
    return set;
  }, [records]);

  const hasAbn = availableStatuses.has("ABN");
  const hasTfn = availableStatuses.has("TFN");

  const summaryMessage = useMemo(() => {
    if (hasAbn && hasTfn) return "You have both an ABN and a TFN record on file.";
    if (hasAbn) return "You have an ABN record on file.";
    if (hasTfn) return "You have a TFN record on file.";
    return "No ABN or TFN records found on your profile.";
  }, [hasAbn, hasTfn]);

  const openForm = (workStatus) => {
    const rec = records.find((r) => r.workStatus === workStatus);
    if (!rec) return;
    setActiveWorkStatus(workStatus);
    setFormData({ ...rec });
    setErrors({});
    setTouched(new Set());
  };

  const closeForm = () => {
    setActiveWorkStatus(null);
    setFormData(null);
    setErrors({});
    setTouched(new Set());
  };

  const validateField = (key, value) => {
    const trimmed = (value ?? "").toString().trim();
    if (!trimmed) return `${FIELD_LABELS[key]} is required`;
    return null;
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => new Set([...prev, key]));

    const err = validateField(key, value);
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next[key] = err;
      else delete next[key];
      return next;
    });
  };

  const isFormValid = useCallback(() => {
    if (!formData) return false;
    for (const key of FIELD_KEYS) {
      if (key === "recordId" || key === "workStatus") continue;
      if (READONLY_FIELDS.has(key)) continue;
      if (!(formData[key] ?? "").toString().trim()) return false;
    }
    return Object.keys(errors).length === 0;
  }, [formData, errors]);

  const handleSubmit = async () => {
    if (!formData || isSubmitting) return;

    const newErrors = {};
    FIELD_KEYS.forEach((key) => {
      if (key === "recordId" || key === "workStatus") return;
      if (READONLY_FIELDS.has(key)) return;
      const err = validateField(key, formData[key]);
      if (err) newErrors[key] = err;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(new Set(Object.keys(FIELD_LABELS)));
      setToast({ type: "error", message: "Please complete all required fields." });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await updatePersonalDetails(formData);
      if (res.status === "success") {
        setToast({
          type: "success",
          message: res.message || "Personal details updated successfully!",
        });
        setRecords((prev) =>
          prev.map((r) => (r.recordId === formData.recordId ? { ...formData } : r))
        );
      } else {
        throw new Error(res.message || "Update failed");
      }
    } catch (err) {
      console.error("Update failed:", err);
      setToast({
        type: "error",
        message: err.message || "Failed to update. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `ob-input ${touched.has(field) && errors[field] ? "ob-input-error" : ""}`;

  const renderField = (key) => {
    const label = FIELD_LABELS[key];
    const readOnly = READONLY_FIELDS.has(key);
    const isDate = DATE_FIELDS.has(key);
    const isSelect = SELECT_FIELDS.has(key) && !readOnly;

    if (isSelect) {
    return (
      <div className="ob-field" key={key}>
        <CustomSelect
          label={label}
          required
          value={formData[key] ?? ""}
          options={SELECT_OPTIONS[key]}
          onChange={(val) => handleChange(key, val)}
        />
        {touched.has(key) && errors[key] && (
          <div className="ob-error">
            <ErrorIcon />
            {errors[key]}
          </div>
        )}
      </div>
    );
  }

    return (
      <div className="ob-field" key={key}>
        <label className="ob-label">
          {label} <span className="ob-label-required">*</span>
        </label>
        <input
          type={isDate ? "date" : "text"}
          value={formData[key] ?? ""}
          onChange={(e) => !readOnly && handleChange(key, e.target.value)}
          onKeyDown={!readOnly ? handleKeyDown : undefined}
          readOnly={readOnly}
          className={readOnly ? "ob-input ob-input-readonly" : inputClass(key)}
          placeholder={readOnly ? "" : `Enter ${label}`}
        />
        {!readOnly && touched.has(key) && errors[key] && (
          <div className="ob-error">
            <ErrorIcon />
            {errors[key]}
          </div>
        )}
      </div>
    );
  };

  // ===== Render =====
  return (
    <div className="ob-container">
      {toast && (
        <div
          className={`ob-toast ${
            toast.type === "success" ? "ob-toast-success" : "ob-toast-error"
          }`}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            {toast.type === "success" ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            )}
          </svg>
          {toast.message}
        </div>
      )}

      {loading && (
        <div className="ob-form-card">
          <div className="ob-info-banner" >
            <LoadingState message={"Loading your personal details…"}/>
            
          </div>
        </div>
      )}
      {!loading && loadError && (
        <div className="ob-form-card">
          <div className="ob-info-banner ob-info-banner-warning">
            <ErrorIcon /> {loadError}
          </div>
        </div>
      )}

      {!loading && !loadError && (
        <>
          {/* Summary + buttons */}
          <div className="ob-form-card">
            <div className="ob-record-card">
              <div className="ob-record-header">
                <div className="ob-record-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="ob-record-title">{summaryMessage}</h3>
              </div>
              <p className="ob-helper" style={{ marginTop: "8px" }}>
                Select a record below to open the form and update your details.
              </p>
            </div>

            <div
              className="ob-form-fields"
              style={{
                display: "flex",
                gap: "16px",
                flexDirection: "row",
                marginTop: "20px",
              }}
            >
              <button
                onClick={() => openForm("ABN")}
                disabled={!hasAbn}
                className={`ob-btn ${
                  activeWorkStatus === "ABN" ? "ob-btn-submit" : "ob-btn-check"
                }`}
                style={{ flex: 1 }}
              >
                <div
                  style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                >
                  <span style={{ fontSize: "16px", fontWeight: 600 }}>ABN</span>
                  <span style={{ fontSize: "12px", opacity: 0.85, marginTop: "2px" }}>
                    {hasAbn ? "click to open form" : "no record"}
                  </span>
                </div>
              </button>

              <button
                onClick={() => openForm("TFN")}
                disabled={!hasTfn}
                className={`ob-btn ${
                  activeWorkStatus === "TFN" ? "ob-btn-submit" : "ob-btn-check"
                }`}
                style={{ flex: 1 }}
              >
                <div
                  style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                >
                  <span style={{ fontSize: "16px", fontWeight: 600 }}>TFN</span>
                  <span style={{ fontSize: "12px", opacity: 0.85, marginTop: "2px" }}>
                    {hasTfn ? "click to open form" : "no record"}
                  </span>
                </div>
              </button>
            </div>
          </div>

          {/* Form card */}
          {formData && activeWorkStatus && (
            <div className="ob-form-card" style={{ marginTop: "20px" }}>
              <div className="ob-form-fields">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <h2 className="ob-helper" style={{ fontSize: "20px", margin: 0, fontWeight: 600 }}>
                    {activeWorkStatus} Record
                  </h2>
                  <button
                    onClick={closeForm}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#6b7280",
                      fontSize: "14px",
                    }}
                  >
                    Close
                  </button>
                </div>

                {EDITABLE_FIELD_ORDER.map(renderField)}

                {/* Banking warning */}
                <div className="ob-divider" />
                <div
                  style={{
                    color: "#991b1b",
                    fontSize: "14px",
                    fontWeight: 400,
                    marginBottom: "8px",
                  }}
                >
                  Banking details are read-only. Please contact admin if you need to
                  change your banking information.
                </div>

                {BANKING_FIELD_ORDER.map(renderField)}

                <button
                  onClick={handleSubmit}
                  disabled={!isFormValid() || isSubmitting}
                  className="ob-btn ob-btn-submit"
                >
                  {isSubmitting ? (
                    <span className="ob-spinner">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Updating…
                    </span>
                  ) : (
                    "Update"
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PersonalDetails;